import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

/**
 * Public embeddable widget — no cookies, no login.
 * EliteBase mints a signed URL server-side via `POST /api/elitebase/widget/token`
 * (auth'd with X-API-Key) and embeds it as an <iframe src="…/embed/learner-history?…">.
 * Signature is validated by `/api/elitebase/widget/data`.
 */
const API = process.env.REACT_APP_BACKEND_URL;

export default function LearnerHistoryEmbed() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const exp = params.get("exp") || "";
  const sig = params.get("sig") || "";

  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get(`${API}/api/elitebase/widget/data`, {
          params: { email, exp, sig },
        });
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Unable to load payment history";
        if (!cancelled) setState({ loading: false, error: String(msg), data: null });
      }
    }
    if (email && exp && sig) load();
    else setState({ loading: false, error: "Missing widget parameters", data: null });
    return () => { cancelled = true; };
  }, [email, exp, sig]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="learner-history-embed">
      <div className="max-w-4xl mx-auto space-y-4">
        <header>
          <h1 className="text-lg font-heading font-bold text-slate-900">Payment history</h1>
          <p className="text-xs text-slate-500 mt-0.5">Approved and sent invoices, verified receipts for <span className="font-mono">{email}</span>.</p>
        </header>

        {state.loading && <SkeletonCard />}

        {state.error && (
          <div className="border border-rose-200 bg-rose-50 text-rose-800 p-4 rounded-md text-sm" data-testid="widget-error">
            {state.error}
          </div>
        )}

        {state.data && (
          <>
            <RecordsSection
              title="Invoices"
              testId="widget-invoices"
              rows={state.data.invoices}
              columns={[
                { key: "invoice_number", label: "Number", mono: true },
                { key: "issue_date", label: "Date" },
                { key: "description", label: "Description", grow: true },
                { key: "total_amount", label: "Total", right: true, currency: true },
                { key: "status", label: "Status", badge: true },
                { key: "pdf_url", label: "", link: "PDF" },
              ]}
            />
            <RecordsSection
              title="Receipts"
              testId="widget-receipts"
              rows={state.data.receipts}
              columns={[
                { key: "receipt_number", label: "Number", mono: true },
                { key: "date", label: "Date" },
                { key: "payment_method", label: "Method" },
                { key: "amount", label: "Amount", right: true, currency: true },
                { key: "status", label: "Status", badge: true },
                { key: "pdf_url", label: "", link: "PDF" },
              ]}
            />
          </>
        )}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6 shadow-sm animate-pulse space-y-3">
      <div className="h-4 w-32 bg-slate-200 rounded"></div>
      <div className="h-3 w-full bg-slate-100 rounded"></div>
      <div className="h-3 w-5/6 bg-slate-100 rounded"></div>
      <div className="h-3 w-2/3 bg-slate-100 rounded"></div>
    </div>
  );
}

function RecordsSection({ title, rows, columns, testId }) {
  const empty = !rows || rows.length === 0;
  return (
    <section className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <span className="text-xs text-slate-500 font-mono">{rows?.length || 0}</span>
      </div>
      {empty ? (
        <div className="p-6 text-center text-sm text-slate-500">No {title.toLowerCase()} yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-2 text-left font-medium ${c.right ? "text-right" : ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id ?? r.number ?? i} className="border-t border-slate-100">
                  {columns.map((c) => (
                    <Cell key={c.key} col={c} row={r} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Cell({ col, row }) {
  const v = row[col.key];
  const base = "px-4 py-2 align-middle";
  if (col.link) {
    return (
      <td className={`${base} text-right`}>
        {v ? <a href={v} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline text-xs font-medium">{col.link} ↗</a> : ""}
      </td>
    );
  }
  if (col.badge) {
    const cls = v === "Sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : v === "Approved" ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-slate-100 text-slate-700";
    return <td className={base}><span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${cls}`}>{v || "—"}</span></td>;
  }
  if (col.currency) {
    return <td className={`${base} text-right font-mono text-xs`}>{row.currency} {v}</td>;
  }
  return <td className={`${base} ${col.mono ? "font-mono text-xs" : "text-xs"} ${col.right ? "text-right" : ""} ${col.grow ? "max-w-md truncate" : ""}`}>{v || "—"}</td>;
}
